import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { POST_GAME_SURVEY_QUESTIONS } from '@/config/empiricalStudy';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { empiricalStudyService } from '@/services/study/empiricalStudyService';
import {
  getStoredEmpiricalStudySession,
  updateEmpiricalStudySession
} from '@/utils/empiricalStudySession';
import { clearStoredSessionData } from '@/utils/sessionRecovery';
import { useGameStore } from '@/stores/gameStore';
import { useGameSessionStore } from '@/stores/gameSessionStore';

const StudyPostGameSurvey = () => {
  const [searchParams] = useSearchParams();
  const navigate = useSafeNavigate();
  const resetGame = useGameStore((state) => state.resetGame);
  const resetSessionStore = useGameSessionStore((state) => state.reset);
  const sessionId = searchParams.get('sessionId') || undefined;
  const storedStudySession = useMemo(
    () => getStoredEmpiricalStudySession(sessionId),
    [sessionId]
  );
  const [responses, setResponses] = useState<Record<string, string>>(
    storedStudySession?.postSurveyResponses || {}
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(
    Boolean(storedStudySession?.postSurveySubmittedAt)
  );
  const [error, setError] = useState<string | null>(null);

  const hasAnsweredAllQuestions = POST_GAME_SURVEY_QUESTIONS.every(
    (question) => Boolean(responses[question.id])
  );

  const handleReturnToLobby = () => {
    resetSessionStore();
    resetGame();
    clearStoredSessionData();
    navigate('/lobby');
  };

  const handleSubmit = async () => {
    if (!storedStudySession?.participantCode || !hasAnsweredAllQuestions) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await empiricalStudyService.submitPostGameSurvey(
        storedStudySession.participantCode,
        responses
      );
      updateEmpiricalStudySession({
        sessionId: storedStudySession.sessionId,
        postSurveyResponses: responses,
        postSurveySubmittedAt: new Date().toISOString()
      });
      setIsSubmitted(true);
    } catch (submitError) {
      console.error('❌ Failed to submit post-game survey:', submitError);
      setError('Failed to submit the post-game survey. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!storedStudySession) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '560px',
          backgroundColor: 'rgba(255,255,255,0.12)',
          borderRadius: '20px',
          padding: '2rem',
          backdropFilter: 'blur(10px)',
          textAlign: 'center'
        }}>
          <h1 style={{ marginTop: 0 }}>Post-Game Survey</h1>
          <p style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
            We could not find your study session details. Please return to the lobby and rejoin if needed.
          </p>
          <button
            type="button"
            onClick={handleReturnToLobby}
            style={{
              marginTop: '1rem',
              padding: '0.9rem 1.5rem',
              borderRadius: '999px',
              border: 'none',
              backgroundColor: '#4CAF50',
              color: '#FFFFFF',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Back to Lobby
          </button>
        </div>
      </div>
    );
  }

  if (storedStudySession.consentChoice !== 'consent') {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '560px',
          backgroundColor: 'rgba(255,255,255,0.12)',
          borderRadius: '20px',
          padding: '2rem',
          backdropFilter: 'blur(10px)',
          textAlign: 'center'
        }}>
          <h1 style={{ marginTop: 0 }}>Post-Game Survey</h1>
          <p style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
            This survey is only shown to students who consented to participate in the study.
          </p>
          <button
            type="button"
            onClick={handleReturnToLobby}
            style={{
              marginTop: '1rem',
              padding: '0.9rem 1.5rem',
              borderRadius: '999px',
              border: 'none',
              backgroundColor: '#4CAF50',
              color: '#FFFFFF',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Back to Lobby
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '720px',
        backgroundColor: 'rgba(255,255,255,0.12)',
        borderRadius: '20px',
        padding: '2rem',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.24)'
      }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Post-Game Survey</h1>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.82)', lineHeight: 1.6 }}>
            Your answers are linked to your hidden study code and will be matched with your pre-game responses and game results.
          </p>
        </div>

        {isSubmitted ? (
          <div style={{
            backgroundColor: 'rgba(34,197,94,0.16)',
            border: '1px solid rgba(34,197,94,0.35)',
            borderRadius: '16px',
            padding: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2.2rem', marginBottom: '0.75rem' }}>✅</div>
            <h2 style={{ marginTop: 0 }}>Survey submitted</h2>
            <p style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
              Thank you. Your post-game survey has been recorded.
            </p>
            <button
              type="button"
              onClick={handleReturnToLobby}
              style={{
                marginTop: '1rem',
                padding: '0.95rem 1.6rem',
                borderRadius: '999px',
                border: 'none',
                backgroundColor: '#4CAF50',
                color: '#FFFFFF',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Back to Lobby
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {POST_GAME_SURVEY_QUESTIONS.map((question) => (
                <div
                  key={question.id}
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    borderRadius: '16px',
                    padding: '1.25rem'
                  }}
                >
                  <div style={{
                    fontWeight: 'bold',
                    marginBottom: '0.9rem',
                    fontSize: '1rem'
                  }}>
                    {question.prompt}
                  </div>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {question.options.map((option) => (
                      <label
                        key={option.value}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.9rem 1rem',
                          borderRadius: '12px',
                          border: responses[question.id] === option.value
                            ? '2px solid #60A5FA'
                            : '1px solid rgba(255,255,255,0.18)',
                          backgroundColor: responses[question.id] === option.value
                            ? 'rgba(96,165,250,0.18)'
                            : 'rgba(255,255,255,0.05)',
                          cursor: 'pointer'
                        }}
                      >
                        <input
                          type="radio"
                          name={question.id}
                          value={option.value}
                          checked={responses[question.id] === option.value}
                          onChange={() => setResponses((prev) => ({
                            ...prev,
                            [question.id]: option.value
                          }))}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {error && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                borderRadius: '12px',
                backgroundColor: 'rgba(239,68,68,0.16)',
                border: '1px solid rgba(239,68,68,0.35)',
                color: '#FECACA'
              }}>
                {error}
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '1rem',
              flexWrap: 'wrap',
              marginTop: '1.5rem'
            }}>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!hasAnsweredAllQuestions || isSubmitting}
                style={{
                  padding: '0.95rem 1.6rem',
                  borderRadius: '999px',
                  border: 'none',
                  backgroundColor: !hasAnsweredAllQuestions || isSubmitting
                    ? 'rgba(255,255,255,0.25)'
                    : '#FF6B35',
                  color: '#FFFFFF',
                  fontWeight: 'bold',
                  cursor: !hasAnsweredAllQuestions || isSubmitting
                    ? 'not-allowed'
                    : 'pointer'
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit post-game survey'}
              </button>

              <button
                type="button"
                onClick={handleReturnToLobby}
                style={{
                  padding: '0.95rem 1.6rem',
                  borderRadius: '999px',
                  border: '1px solid rgba(255,255,255,0.25)',
                  backgroundColor: 'rgba(255,255,255,0.12)',
                  color: '#FFFFFF',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Skip for now
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StudyPostGameSurvey;
